import {
  EColorPickerTab,
  EProjectType,
  IImageLibraryDto,
  InvisibleStylePart,
  isGradient,
  ISavedColor,
  Visibility
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import React, { Component } from "react";
import { SketchPicker } from "react-color";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { ColorPickerPositionHelper } from "../../../../../shared/utility/color-picker-position-helper";
import { getSdInstance } from "../../../../../shared/utility/designer-context";
import {
  addDesignerProjectColor,
  deleteDesignerProjectColor,
  listDesignerProjectColors,
  updateDesignerProjectColor
} from "../../../../../shared/utility/designer-project-color-actions";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../../../ui-kit/typography/TextMedium/TextMedium";
import AddColorPopover from "./add-color-popover";
import { AIImageGenerator } from "./ai-image-generator";
import ColorList from "./color-list";
import GradientPicker from "./gradient-picker";
import {
  buildGradientString,
  DEFAULT_GRADIENT_POSITION,
  DEFAULT_LINEAR_ANGLE,
  DEFAULT_RADIAL_SHAPE,
  DEFAULT_RADIAL_SIZE,
  GradientType,
  IGradientPosition,
  IGradientState,
  parseGradient,
  RadialShape,
  RadialSize
} from "./gradient-utils";
import ImagePicker from "./image-picker";
import "./styling-color-picker.scss";

interface IColorPickerState {
  colorList: ISavedColor[];
  newColorName: string;
  activeTab: EColorPickerTab;
  gradient: IGradientState;
  selectedStopIndex: number;
  isDragging: boolean;
  gradientInputValue: string;
  isSketchPickerVisible: boolean;
  dragOffset: number;
  selectedImageId?: string;
  selectedFit?: string;
  selectedPosition?: string;
  isLoadingImages: boolean;
  isEditMode: boolean;
  editingColor: ISavedColor | null;
  editColorName: string;
  position: { x: number; y: number };
  isDraggingColorPicker: boolean;
  dragStartPosition: { x: number; y: number };
  isAddColorPopoverVisible: boolean;
  hasLoadedImages: boolean;
  openedWithDiId?: number;
}

interface IColorPickerProps {
  onChange?: (color: string) => any;
  color?: string;
  onCancel?: (e?: React.MouseEvent<HTMLDivElement>) => any;
  isColorPickerActive: boolean;
  isSavedColorsHidden?: boolean;
  isHex?: boolean;
  onImageFitChange?: (fit: string) => void;
  handleBgImagePositionChange?: (value: string) => void;
  onBgImageChange?: (image: IImageLibraryDto) => void;
  setBgImageNone?: () => void;
  initialImageId?: string;
  initialImageFit?: string;
  initialImagePosition?: string;
  hideHeaderTabs?: boolean;
  clickPosition?: { x: number; y: number };
  allImage?: IImageLibraryDto[];
  getImageLibrary?: () => Promise<void>;
  isDataLoading?: boolean;
  pickerType?: "color" | "image";
}

const DEFAULT_COLORS: ISavedColor[] = [
  { id: "1", name: "Transparent", color: "transparent", isDefault: true },
  { id: "2", name: "White", color: "rgba(255, 255, 255, 1)", isDefault: true },
  { id: "3", name: "Light Gray", color: "rgba(217, 217, 217, 1)", isDefault: true },
  { id: "4", name: "Gray", color: "rgba(140, 140, 140, 1)", isDefault: true },
  { id: "5", name: "Black", color: "rgba(0, 0, 0, 1)", isDefault: true },
  { id: "6", name: "Red", color: "rgba(225, 60, 57, 1)", isDefault: true },
  { id: "7", name: "Orange", color: "rgba(231, 96, 51, 1)", isDefault: true },
  { id: "8", name: "Pumpkin Orange", color: "rgba(235, 145, 58, 1)", isDefault: true },
  { id: "9", name: "Yellow", color: "rgba(245, 220, 77, 1)", isDefault: true },
  { id: "10", name: "Lime Green", color: "rgba(172, 215, 70, 1)", isDefault: true },
  { id: "11", name: "Green", color: "rgba(114, 194, 64, 1)", isDefault: true },
  { id: "12", name: "Turquoise", color: "rgba(90, 191, 193, 1)", isDefault: true },
  { id: "13", name: "Blue", color: "rgba(68, 142, 247, 1)", isDefault: true },
  { id: "14", name: "Purple", color: "rgba(106, 50, 201, 1)", isDefault: true },
  { id: "15", name: "Pink", color: "rgba(216, 66, 147, 1)", isDefault: true }
];

class ColorPicker extends Component<IColorPickerProps & IWithSDProps, IColorPickerState> {
  private colorPickerRef = React.createRef<HTMLDivElement>();

  constructor(props: IColorPickerProps & IWithSDProps) {
    super(props);
    this.state = {
      colorList: DEFAULT_COLORS,
      newColorName: "",
      activeTab: EColorPickerTab.Solid,
      gradient: {
        type: "linear",
        angle: 0,
        stops: []
      },
      selectedStopIndex: 0,
      isDragging: false,
      gradientInputValue: "",
      isSketchPickerVisible: false,
      dragOffset: 0,
      selectedImageId: props.initialImageId,
      selectedFit: props.initialImageFit,
      selectedPosition: props.initialImagePosition,
      isLoadingImages: false,
      isEditMode: false,
      editingColor: null,
      editColorName: "",
      position: { x: 0, y: 0 },
      isDraggingColorPicker: false,
      dragStartPosition: { x: 0, y: 0 },
      isAddColorPopoverVisible: false,
      hasLoadedImages: false
    };
  }

  componentDidUpdate(prevProps: IColorPickerProps & IWithSDProps) {
    if (this.props.isColorPickerActive && this.state.openedWithDiId) {
      const currentSelectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
      if (currentSelectedDi?.id !== this.state.openedWithDiId) {
        this.props.onCancel?.();
        return;
      }
    }

    if (prevProps.isColorPickerActive !== this.props.isColorPickerActive) {
      if (this.props.isColorPickerActive) {
        const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
        this.getSavedColors();
        this.initializeGradientIfNeeded();

        const position = this.calculatePosition();
        this.setState({ position, openedWithDiId: selectedDi?.id });
        ColorPickerPositionHelper.saveOpenPickerPosition(position);

        if (this.props.pickerType === "image" || this.isImage()) {
          if (this.props.getImageLibrary && !this.state.hasLoadedImages) {
            this.props.getImageLibrary();
            this.setState({ hasLoadedImages: true });
          }
          this.setState({
            selectedImageId: this.props.initialImageId,
            selectedFit: this.props.initialImageFit,
            selectedPosition: this.props.initialImagePosition,
            activeTab: EColorPickerTab.Image
          });
        } else {
          this.setState({
            activeTab:
              this.props.color && isGradient(this.props.color) ? EColorPickerTab.Gradient : EColorPickerTab.Solid
          });
        }
      } else {
        this.setState({ position: { x: 0, y: 0 }, openedWithDiId: undefined });
        ColorPickerPositionHelper.clearOpenPickerPosition();
      }
    } else if (this.props.isColorPickerActive && this.state.position.x === 0 && this.state.position.y === 0) {
      // Picker is active but its position was reset to {0,0} by a re-render (e.g.
      // a style commit while editing). Restore the position saved while it was
      // open instead of recomputing from a stale click.
      const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
      const savedPosition = ColorPickerPositionHelper.getOpenPickerPosition();
      const restoredPosition = savedPosition ?? this.calculatePosition();
      this.setState({
        position: restoredPosition,
        openedWithDiId: this.state.openedWithDiId ?? selectedDi?.id
      });
      ColorPickerPositionHelper.saveOpenPickerPosition(restoredPosition);
    }

    if (prevProps.color !== this.props.color) {
      if (this.state.activeTab === EColorPickerTab.Gradient) {
        const defaultStops = [
          { color: "rgba(36, 109, 255, 1)", position: 0 },
          { color: "rgba(255, 255, 255, 1)", position: 100 }
        ];

        const parsed = parseGradient(this.props.color);
        if (parsed) {
          const normalized = this.normalizeGradientForCapability(parsed);
          this.setState({
            gradient: {
              ...normalized,
              stops: normalized.stops.length > 0 ? normalized.stops : defaultStops
            }
          });
        }
      }
    }

    if (prevProps.initialImageId !== this.props.initialImageId) {
      this.setState({ selectedImageId: this.props.initialImageId });
    }
    if (prevProps.initialImageFit !== this.props.initialImageFit) {
      this.setState({ selectedFit: this.props.initialImageFit });
    }
    if (prevProps.initialImagePosition !== this.props.initialImagePosition) {
      this.setState({ selectedPosition: this.props.initialImagePosition });
    }
  }

  componentDidMount() {
    this.getSavedColors();
    this.initializeGradientIfNeeded();
    // Remove redundant image library call - it will be loaded when needed
    document.addEventListener("mousemove", this.handleColorPickerDrag);
    document.addEventListener("mouseup", this.handleColorPickerDragEnd);
    document.addEventListener("mousedown", this.handleOutsideClick);
    window.addEventListener("resize", this.handleWindowResize);
  }

  componentWillUnmount() {
    // Clean up all event listeners
    document.removeEventListener("mousemove", this.handleColorPickerDrag);
    document.removeEventListener("mouseup", this.handleColorPickerDragEnd);
    document.removeEventListener("mousedown", this.handleOutsideClick);
    window.removeEventListener("resize", this.handleWindowResize);

    // Clean up drag-related event listeners that may have been added during drag operations
    document.removeEventListener("mousemove", this.handleStopDrag);
    document.removeEventListener("mouseup", this.handleStopDragEnd);

    // Forget the persisted picker position when the component goes away.
    ColorPickerPositionHelper.clearOpenPickerPosition();
  }

  getSavedColors = () => {
    const result = listDesignerProjectColors(getSdInstance());
    if ("error" in result) return;
    this.setState({ colorList: [...DEFAULT_COLORS, ...result.colors] });
  };

  getColorPickerHeight = () => {
    const isImagePicker = this.props.pickerType === "image" || this.isImage();
    const hasSelectedImage = this.state.selectedImageId;

    if (isImagePicker && hasSelectedImage) {
      return 560;
    }

    return 500;
  };

  // The fixed-position picker is painted from `state.position`, which is only
  // assigned post-commit in componentDidUpdate; until then it holds the reset
  // sentinel {0,0}, i.e. the top-left corner. That assignment is gated on the
  // isColorPickerActive prop *transitioning*, but a context-driven re-render can
  // arrive with the prop already settled and skip it, leaving the picker stuck at
  // (0,0). Derive the paint position at render time so it is correct regardless
  // of lifecycle timing. calculatePosition always clamps with padding > 0, so a
  // computed result is never {0,0} — which keeps {0,0} an unambiguous "not yet
  // computed" marker.
  getEffectivePosition = () => {
    const { position } = this.state;
    if (this.props.isColorPickerActive && position.x === 0 && position.y === 0) {
      // Painted from the saved position if a re-render reset state to {0,0}.
      const savedPosition = ColorPickerPositionHelper.getOpenPickerPosition();
      if (savedPosition) {
        return savedPosition;
      }
      return this.calculatePosition();
    }
    return position;
  };

  calculatePosition = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const colorPickerWidth = 254;
    const colorPickerHeight = this.getColorPickerHeight();
    const padding = 10;

    let x;
    let y;

    const lastClickEvent = ColorPickerPositionHelper.getLastClickEvent();
    const clickPosition =
      this.props.clickPosition ||
      (lastClickEvent && {
        x: lastClickEvent.clientX,
        y: lastClickEvent.clientY
      });

    const hasCurrentPosition = this.state && (this.state.position.x !== 0 || this.state.position.y !== 0);

    if (clickPosition && !hasCurrentPosition) {
      x = clickPosition.x + 100;
      y = clickPosition.y;
    } else if (hasCurrentPosition) {
      x = this.state.position.x;
      y = this.state.position.y;
    } else {
      x = viewportWidth - colorPickerWidth - 20;
      y = 20;
    }

    x = Math.max(padding, Math.min(x, viewportWidth - colorPickerWidth - padding));
    y = Math.max(padding, Math.min(y, viewportHeight - colorPickerHeight - padding));

    return { x, y };
  };

  getFontColor = () => {
    if (!this.props.color) {
      return "transparent";
    }

    if (isGradient(this.props.color)) {
      if (this.state.selectedStopIndex !== -1 && this.state.gradient.stops[this.state.selectedStopIndex]) {
        return this.state.gradient.stops[this.state.selectedStopIndex].color;
      }
      return "transparent";
    }

    return this.props.color;
  };

  setFontColor = (color: any) => {
    if (this.state.activeTab === EColorPickerTab.Solid) {
      if (color.rgb.a === 0 && color.rgb.r !== 0 && color.rgb.g !== 0 && color.rgb.b !== 0) {
        color.rgb.a = 1;
      }
      if (this.props.onChange) {
        if (this.props.isHex) {
          this.props.onChange(`${color.hex.toUpperCase()}`);
        } else {
          this.props.onChange(`rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`);
        }
      }
    } else if (this.state.activeTab === EColorPickerTab.Gradient) {
      const { gradient, selectedStopIndex } = this.state;
      if (selectedStopIndex !== -1) {
        const newStops = [...gradient.stops];
        newStops[selectedStopIndex] = {
          ...newStops[selectedStopIndex],
          color: `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`
        };
        this.setState(
          {
            gradient: {
              ...gradient,
              stops: newStops
            }
          },
          () => {
            if (this.props.onChange) {
              this.props.onChange(this.getGradientString());
            }
          }
        );
      }
    }
  };

  // Select a picker field (HEX / R / G / B / A) on focus so typing replaces the
  // shown value instead of appending. rAF re-select survives react-color's own
  // post-focus re-render, which can otherwise leave only part selected.
  handleSketchFieldFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    const target: HTMLElement = e.target as HTMLElement;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    target.select();
    requestAnimationFrame(() => {
      if (document.activeElement === target) {
        target.select();
      }
    });
  };

  setFontColorFromList = (color: string) => {
    if (this.props.onChange) {
      this.props.onChange(color);
    }
  };

  addColor = () => {
    const { newColorName } = this.state;
    let color = this.getFontColor();
    if (!color || color === "transparent") {
      return;
    }
    if (newColorName && color) {
      const result = addDesignerProjectColor(getSdInstance(), {
        id: Guid.create().toString(),
        name: newColorName,
        color
      });
      if ("error" in result) {
        KMainFunctions.displayWarningNotification(result.error);
        return;
      }
      this.setState({
        newColorName: ""
      });
      this.getSavedColors();
    }
  };

  handleDeleteColor = (id: string) => {
    deleteDesignerProjectColor(getSdInstance(), id);
    this.getSavedColors();
  };

  handleAddColorPopoverVisibleChange = (visible: boolean) => {
    this.setState({ isAddColorPopoverVisible: visible });
  };

  handleTabChange = (tab: EColorPickerTab, event?: MouseEvent) => {
    this.setState(
      {
        activeTab: tab,
        selectedStopIndex: 0,
        isSketchPickerVisible: false
      },
      () => {
        if (tab === EColorPickerTab.Solid) {
          const parsedForSolid = parseGradient(this.props.color);
          if (parsedForSolid) {
            const solidColor =
              parsedForSolid.stops.length > 0 ? parsedForSolid.stops[0].color : "rgba(36, 109, 255, 1)";
            if (this.props.onChange) {
              this.props.onChange(solidColor);
            }
          } else if (!this.props.color || this.props.color === "transparent") {
            if (this.props.onChange) {
              this.props.onChange("rgba(36, 109, 255, 1)");
            }
          }
        } else if (tab === EColorPickerTab.Gradient) {
          const defaultStops = [
            { color: "rgba(36, 109, 255, 1)", position: 0 },
            { color: "rgba(255, 255, 255, 1)", position: 100 }
          ];

          let gradientToApply: IGradientState | undefined;
          const parsedGradient = parseGradient(this.props.color);

          if (parsedGradient) {
            const normalized = this.normalizeGradientForCapability(parsedGradient);
            const finalStops = normalized.stops.length > 0 ? normalized.stops : defaultStops;
            gradientToApply = {
              ...normalized,
              stops: finalStops
            };
            this.setState({
              gradient: gradientToApply,
              gradientInputValue: KMainFunctions.convertRgbaToHex(finalStops[0].color)
            });
          } else if (this.props.color && this.props.color !== "transparent") {
            const solidColor = this.props.color;
            const gradientStops = [
              { color: solidColor, position: 0 },
              { color: "rgba(255, 255, 255, 1)", position: 100 }
            ];
            gradientToApply = {
              type: "linear",
              angle: DEFAULT_LINEAR_ANGLE,
              stops: gradientStops
            };
            this.setState({
              gradient: gradientToApply,
              gradientInputValue: KMainFunctions.convertRgbaToHex(gradientStops[0].color)
            });
          } else if (this.state.gradient.stops.length === 0) {
            gradientToApply = {
              type: "linear",
              angle: DEFAULT_LINEAR_ANGLE,
              stops: defaultStops
            };
            this.setState({
              gradient: gradientToApply,
              gradientInputValue: KMainFunctions.convertRgbaToHex(defaultStops[0].color)
            });
          } else {
            gradientToApply = this.state.gradient;
            this.setState({
              gradientInputValue: KMainFunctions.convertRgbaToHex(gradientToApply.stops[0].color)
            });
          }

          if (this.props.onChange && gradientToApply) {
            this.props.onChange(buildGradientString(gradientToApply));
          }
        }
        if (event) {
          KMainFunctions.setColorTriggeredPickerPosition(event, tab);
        }
      }
    );
  };

  handleAngleChange = (newAngle: number) => {
    const normalizedAngle = ((newAngle % 360) + 360) % 360;
    this.setState(
      {
        gradient: {
          ...this.state.gradient,
          angle: normalizedAngle
        }
      },
      () => {
        if (this.props.onChange) {
          this.props.onChange(this.getGradientString());
        }
      }
    );
  };

  handleStopSelect = (index: number) => {
    const selectedStop = this.state.gradient.stops[index];
    this.setState({
      selectedStopIndex: index,
      gradientInputValue: selectedStop ? KMainFunctions.convertRgbaToHex(selectedStop.color) : "",
      isSketchPickerVisible: false
    });
  };

  handleStopColorChange = (index: number, color: any) => {
    const newColor = `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`;
    const newStops = [...this.state.gradient.stops];
    newStops[index] = {
      ...newStops[index],
      color: newColor
    };

    this.setState(
      {
        gradient: {
          ...this.state.gradient,
          stops: newStops
        },
        gradientInputValue: KMainFunctions.convertRgbaToHex(newColor)
      },
      () => {
        if (this.props.onChange) {
          this.props.onChange(this.getGradientString());
        }
      }
    );
  };

  handleSwapColors = () => {
    const { stops } = this.state.gradient;
    if (stops.length >= 2 && this.state.selectedStopIndex !== -1) {
      const currentIndex = this.state.selectedStopIndex;
      const nextIndex = currentIndex === stops.length - 1 ? currentIndex - 1 : currentIndex + 1;

      const newStops = [...stops];
      const temp = newStops[currentIndex].color;
      newStops[currentIndex].color = newStops[nextIndex].color;
      newStops[nextIndex].color = temp;

      this.setState(
        {
          gradient: {
            ...this.state.gradient,
            stops: newStops
          },
          selectedStopIndex: nextIndex
        },
        () => {
          if (this.props.onChange) {
            this.props.onChange(this.getGradientString());
          }
        }
      );
    }
  };

  handleStopMouseDown = (event: React.MouseEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();

    const previewElement = document.querySelector(".gradient-picker__preview");
    if (!previewElement) return;

    this.setState({
      isDragging: true,
      selectedStopIndex: index,
      isSketchPickerVisible: false
    });

    document.addEventListener("mousemove", this.handleStopDrag);
    document.addEventListener("mouseup", this.handleStopDragEnd);
  };

  handleStopDrag = (event: MouseEvent) => {
    if (!this.state.isDragging) return;

    const previewElement = document.querySelector(".gradient-picker__preview");
    if (!previewElement) return;

    const rect = previewElement.getBoundingClientRect();
    const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const position = Math.max(0, Math.min(100, (x / rect.width) * 100));

    const newStops = [...this.state.gradient.stops];
    const selectedIndex = this.state.selectedStopIndex;

    if (selectedIndex !== -1) {
      const draggedStop = {
        ...newStops[selectedIndex],
        position: Math.round(position)
      };

      newStops[selectedIndex] = draggedStop;
      newStops.sort((a, b) => a.position - b.position);

      const newSelectedIndex = newStops.findIndex((stop) => stop.color === draggedStop.color);

      this.setState(
        {
          gradient: {
            ...this.state.gradient,
            stops: newStops
          },
          selectedStopIndex: newSelectedIndex
        },
        () => {
          if (this.props.onChange) {
            this.props.onChange(this.getGradientString());
          }
        }
      );
    }
  };

  handleStopDragEnd = () => {
    this.setState({
      isDragging: false
    });
    // Always clean up drag event listeners
    document.removeEventListener("mousemove", this.handleStopDrag);
    document.removeEventListener("mouseup", this.handleStopDragEnd);
  };

  getGradientString = (): string => {
    return buildGradientString(this.state.gradient);
  };

  getAllowAdvancedGradient = (): boolean => {
    const isDesktop: boolean = this.props.sd?.project?.projectType === EProjectType.Desktop;
    const isWebScreen: boolean = this.props.sd?.getIsWebScreen?.() ?? false;
    return isDesktop || isWebScreen;
  };

  // On a surface that cannot render radial/conic (mobile-native screen), present a
  // cross-screen radial/conic value as a linear editor over the same stops. The
  // persisted value is untouched until the user edits; because the working type is
  // already linear, the first edit normalizes the emitted CSS to linear-gradient.
  normalizeGradientForCapability = (gradient: IGradientState): IGradientState => {
    if (this.getAllowAdvancedGradient() || gradient.type === "linear") {
      return gradient;
    }
    return { type: "linear", angle: DEFAULT_LINEAR_ANGLE, stops: gradient.stops };
  };

  handleGradientTypeChange = (type: GradientType): void => {
    const current: IGradientState = this.state.gradient;
    const next: IGradientState = {
      ...current,
      type,
      position: type === "linear" ? current.position : (current.position ?? { ...DEFAULT_GRADIENT_POSITION }),
      shape: type === "radial" ? (current.shape ?? DEFAULT_RADIAL_SHAPE) : current.shape,
      size: type === "radial" ? (current.size ?? DEFAULT_RADIAL_SIZE) : current.size
    };
    this.setState({ gradient: next }, () => {
      if (this.props.onChange) {
        this.props.onChange(buildGradientString(next));
      }
    });
  };

  handleGradientPositionChange = (position: IGradientPosition): void => {
    this.setState({ gradient: { ...this.state.gradient, position } }, () => {
      if (this.props.onChange) {
        this.props.onChange(this.getGradientString());
      }
    });
  };

  handleGradientShapeChange = (shape: RadialShape): void => {
    this.setState({ gradient: { ...this.state.gradient, shape } }, () => {
      if (this.props.onChange) {
        this.props.onChange(this.getGradientString());
      }
    });
  };

  handleGradientSizeChange = (size: RadialSize): void => {
    this.setState({ gradient: { ...this.state.gradient, size } }, () => {
      if (this.props.onChange) {
        this.props.onChange(this.getGradientString());
      }
    });
  };

  isImage = (): boolean => {
    return this.props.pickerType === "image" || this.props.initialImageId !== undefined;
  };

  initializeGradientIfNeeded = () => {
    const color = this.props.color;
    const defaultStops = [
      { color: "rgba(36, 109, 255, 1)", position: 0 },
      { color: "rgba(255, 255, 255, 1)", position: 100 }
    ];

    const parsed = parseGradient(color);
    if (parsed) {
      const normalized = this.normalizeGradientForCapability(parsed);
      const initialStops = normalized.stops.length > 0 ? normalized.stops : defaultStops;
      this.setState({
        activeTab: EColorPickerTab.Gradient,
        gradient: {
          ...normalized,
          stops: initialStops
        },
        selectedStopIndex: 0,
        isSketchPickerVisible: false,
        gradientInputValue: KMainFunctions.convertRgbaToHex(initialStops[0].color)
      });
    } else if (this.state.gradient.stops.length === 0) {
      this.setState({
        gradient: {
          type: "linear",
          angle: DEFAULT_LINEAR_ANGLE,
          stops: defaultStops
        },
        selectedStopIndex: 0,
        isSketchPickerVisible: false,
        gradientInputValue: KMainFunctions.convertRgbaToHex(defaultStops[0].color)
      });
    }
  };

  handleInputClick = () => {
    if (this.state.selectedStopIndex !== -1) {
      this.setState((prevState) => ({
        isSketchPickerVisible: !prevState.isSketchPickerVisible
      }));
    }
  };

  handleFitChange = (fit: string) => {
    this.setState({ selectedFit: fit });
  };

  handlePositionChange = (position: string) => {
    this.setState({ selectedPosition: position });
  };

  handleUploadAIImage = async (
    images: IImageLibraryDto[],
    cb?: { onSuccess?: () => void; onError?: () => void; onFinish?: () => void }
  ) => {
    try {
      const res = await ImageLibraryService.AddOrUpdateResource(images);
      if (res && res.status === 200) {
        if (cb?.onSuccess) cb.onSuccess();

        this.setState({ hasLoadedImages: false }, () => {
          if (this.props.getImageLibrary) {
            this.props.getImageLibrary();
            this.setState({ hasLoadedImages: true });
          }
        });
      }
    } catch (error) {
      if (cb?.onError) cb.onError();

      KMainFunctions.exceptionHandler(error);
    } finally {
      if (cb?.onFinish) cb.onFinish();
    }
  };

  handleEditColor = (color: ISavedColor) => {
    this.setState({
      isEditMode: true,
      editingColor: color,
      editColorName: color.name
    });
  };

  handleSaveColorChanges = async () => {
    const { editingColor, editColorName } = this.state;
    if (editingColor && editColorName) {
      const updatedColor = {
        ...editingColor,
        name: editColorName,
        color: this.getFontColor()
      };

      const result = await updateDesignerProjectColor(getSdInstance(), {
        id: updatedColor.id,
        name: editColorName,
        color: updatedColor.color
      });
      if ("error" in result) {
        KMainFunctions.displayWarningNotification(result.error);
        return;
      }

      this.setState({
        isEditMode: false,
        editingColor: null,
        editColorName: ""
      });

      this.getSavedColors();
    }
  };

  handleCancelEdit = () => {
    this.setState({
      isEditMode: false,
      editingColor: null,
      editColorName: ""
    });
  };

  handleColorPickerDragStart = (event: React.MouseEvent) => {
    if (!(event.target as HTMLElement).closest(".kuika_custom-color-picker__header")) {
      return;
    }

    this.setState({
      isDraggingColorPicker: true,
      dragStartPosition: {
        x: event.clientX - this.state.position.x,
        y: event.clientY - this.state.position.y
      }
    });

    event.preventDefault();
  };

  handleColorPickerDrag = (event: MouseEvent) => {
    if (!this.state.isDraggingColorPicker) return;

    const newX = event.clientX - this.state.dragStartPosition.x;
    const newY = event.clientY - this.state.dragStartPosition.y;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const colorPickerWidth = 254;
    const colorPickerHeight = this.getColorPickerHeight();
    const padding = 10;

    const boundedX = Math.max(padding, Math.min(newX, viewportWidth - colorPickerWidth - padding));
    const boundedY = Math.max(padding, Math.min(newY, viewportHeight - colorPickerHeight - padding));

    this.setState({
      position: {
        x: boundedX,
        y: boundedY
      }
    });
    ColorPickerPositionHelper.saveOpenPickerPosition({ x: boundedX, y: boundedY });

    event.preventDefault();
  };

  handleColorPickerDragEnd = () => {
    if (this.state.isDraggingColorPicker) {
      this.setState({ isDraggingColorPicker: false });
    }
  };

  handleOutsideClick = (event: MouseEvent) => {
    if (!this.props.isColorPickerActive) return;

    const target = event.target as HTMLElement;
    const colorPickerElement = this.colorPickerRef.current;

    const isWithinPopover = target.closest(".kuika-popover");
    const isWithinSelectDropdown = target.closest(".kuika-selectbox__dropdown");

    if (colorPickerElement && !colorPickerElement.contains(target) && !isWithinPopover && !isWithinSelectDropdown) {
      this.props.onCancel?.();
    }
  };

  handleWindowResize = () => {
    if (!this.props.isColorPickerActive) return;

    requestAnimationFrame(() => {
      const position = this.calculatePosition();
      this.setState({ position });
    });
  };

  renderTabContent = () => {
    const {
      activeTab,
      gradient,
      selectedStopIndex,
      isSketchPickerVisible,
      gradientInputValue,
      isEditMode,
      editColorName
    } = this.state;

    switch (activeTab) {
      case EColorPickerTab.Solid:
        return (
          <>
            <div className="kuika-padding-left-s kuika-padding-right-s" style={{ lineHeight: "17px" }}>
              <KuikaSmallText>
                Select a color or type a color value to save it from the + button at the top.
              </KuikaSmallText>
            </div>
            {isEditMode && (
              <div className="edit-color-header">
                <div className="edit-color-actions">
                  <KuikaButton type="primary" size="sm" onClick={this.handleSaveColorChanges} uppercase>
                    <KuikaSmallMediumText>Save Changes</KuikaSmallMediumText>
                  </KuikaButton>
                </div>
                <div className="edit-color-name-field">
                  <KuikaSmallMediumText>Name</KuikaSmallMediumText>
                  <KuikaInput
                    value={editColorName}
                    onChange={(e) => this.setState({ editColorName: e.target.value })}
                    size="sm"
                    placeholder="Color name"
                  />
                </div>
              </div>
            )}
            <div onFocus={this.handleSketchFieldFocus}>
              <SketchPicker
                color={this.getFontColor()}
                onChange={this.setFontColor}
                presetColors={[]}
                styles={{
                  default: {
                    saturation: {
                      height: "100px",
                      padding: "0"
                    }
                  }
                }}
              />
            </div>
          </>
        );
      case EColorPickerTab.Gradient:
        return (
          <GradientPicker
            gradient={gradient}
            selectedStopIndex={selectedStopIndex}
            isSketchPickerVisible={isSketchPickerVisible}
            setIsSketchPickerVisible={(visible) => this.setState({ isSketchPickerVisible: visible })}
            gradientInputValue={gradientInputValue}
            allowAdvancedGradient={this.getAllowAdvancedGradient()}
            onTypeChange={this.handleGradientTypeChange}
            onAngleChange={this.handleAngleChange}
            onPositionChange={this.handleGradientPositionChange}
            onShapeChange={this.handleGradientShapeChange}
            onSizeChange={this.handleGradientSizeChange}
            onStopSelect={this.handleStopSelect}
            onStopColorChange={this.handleStopColorChange}
            onSwapColors={this.handleSwapColors}
            onStopMouseDown={this.handleStopMouseDown}
            onInputClick={this.handleInputClick}
          />
        );
      case EColorPickerTab.Image:
        return (
          <ImagePicker
            allImage={this.props.allImage || []}
            onImageSelect={(image) => this.props.onBgImageChange?.(image)}
            onFitChange={(fit) => this.props.onImageFitChange?.(fit)}
            onPositionChange={(position) => this.props.handleBgImagePositionChange?.(position)}
            setBgImageNone={this.props.setBgImageNone}
            selectedImageId={this.state.selectedImageId}
            selectedFit={this.state.selectedFit}
            selectedPosition={this.state.selectedPosition}
            getImageLibrary={this.props.getImageLibrary || (() => Promise.resolve())}
            isLoading={this.props.isDataLoading || false}
            acceptedFileTypes=".jpg,.png,.svg,.xml"
            fileSize={10}
          />
        );
      case EColorPickerTab.AI:
        return <AIImageGenerator uploadImage={this.handleUploadAIImage} isUploaderBusy={this.state.isLoadingImages} />;
      default:
        return null;
    }
  };

  isStyleHidden = (styleName: InvisibleStylePart): boolean => {
    return this.props.sd.getStylesVisibility(styleName) === Visibility.Hidden;
  };

  render() {
    const { newColorName, colorList, activeTab, isEditMode, isDraggingColorPicker } = this.state;
    const position = this.getEffectivePosition();
    const currentHexColor = KMainFunctions.convertRgbaToHex(this.getFontColor());
    const { pickerType = "color" } = this.props;

    const colorPickerStyle = {
      position: "fixed" as const,
      left: `${position.x}px`,
      top: `${position.y}px`,
      zIndex: 1000,
      cursor: isDraggingColorPicker ? "grabbing" : "default"
    };

    const style = this.props.isColorPickerActive ? colorPickerStyle : {};

    const colorPickerClassName = `kuika_custom-color-picker${isDraggingColorPicker ? " dragging" : ""}`;

    const showColorTabs = pickerType === "color";
    const showImageTabs = pickerType === "image";

    return (
      <div
        ref={this.colorPickerRef}
        className={colorPickerClassName}
        style={style}
        onMouseDown={this.handleColorPickerDragStart}
      >
        <div className="kuika_custom-color-picker__header">
          {this.props.hideHeaderTabs ? (
            <div></div>
          ) : (
            <div className="kuika_custom-color-picker__header__icons">
              {showColorTabs && (
                <>
                  {!this.isStyleHidden(InvisibleStylePart.Fill_Color) && (
                    <KuikaTooltip content="Solid" position="bottom">
                      <KuikaButton
                        type="tertiary"
                        color="neutral"
                        size="sm"
                        isIcon
                        active={activeTab === EColorPickerTab.Solid}
                        onClick={(event) => this.handleTabChange(EColorPickerTab.Solid, event)}
                      >
                        <KuikaSvg name="solid" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  )}
                  {!this.isStyleHidden(InvisibleStylePart.Fill_Color) && (
                    <KuikaTooltip content="Gradient" position="bottom">
                      <KuikaButton
                        type="tertiary"
                        color="neutral"
                        size="sm"
                        isIcon
                        active={activeTab === EColorPickerTab.Gradient}
                        onClick={(event) => this.handleTabChange(EColorPickerTab.Gradient, event)}
                      >
                        <KuikaSvg name="gradient" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  )}
                </>
              )}
              {showImageTabs && (
                <>
                  {!this.isStyleHidden(InvisibleStylePart.Fill_BackgroundImage) && (
                    <KuikaTooltip content="Image" position="bottom">
                      <KuikaButton
                        data-testid="stylingFillImageModalImageTabButton"
                        type="tertiary"
                        color="neutral"
                        size="sm"
                        isIcon
                        active={activeTab === EColorPickerTab.Image}
                        onClick={(event) => this.handleTabChange(EColorPickerTab.Image, event)}
                      >
                        <KuikaSvg name="image-in-picture" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  )}
                  {!this.isStyleHidden(InvisibleStylePart.Fill_BackgroundImage) && (
                    <KuikaTooltip content="Create new image with AI" position="bottom">
                      <KuikaButton
                        data-testid="stylingFillImageModalAiTabButton"
                        type="tertiary"
                        color="neutral"
                        size="sm"
                        isIcon
                        active={activeTab === EColorPickerTab.AI}
                        onClick={(event) => this.handleTabChange(EColorPickerTab.AI, event)}
                        className="kuika_custom-color-picker__header__icons__ai"
                      >
                        <KuikaSvg name="sparkles" type="datasource-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  )}
                </>
              )}
            </div>
          )}
          <div className="kuika_custom-color-picker__header__icons">
            <KuikaTooltip content="Move" position="bottom">
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                isIcon
                className="kuika_custom-color-picker__header__icons__grip"
              >
                <KuikaSvg name="grip-vertical" width={16} height={16} />
              </KuikaButton>
            </KuikaTooltip>
            {!isEditMode && showColorTabs && this.state.activeTab === EColorPickerTab.Solid && (
              <AddColorPopover
                onAddColor={this.addColor}
                newColorName={newColorName}
                onNameChange={(value) => this.setState({ newColorName: value })}
                currentColor={currentHexColor}
                visible={this.state.isAddColorPopoverVisible}
                onVisibleChange={this.handleAddColorPopoverVisibleChange}
              >
                <KuikaButton type="tertiary" color="neutral" size="sm" isIcon>
                  <KuikaSvg name="plus" />
                </KuikaButton>
              </AddColorPopover>
            )}
            <KuikaTooltip content="Close" position="bottom">
              <KuikaButton type="tertiary" color="neutral" size="sm" isIcon onClick={(e) => this.props.onCancel?.(e)}>
                <KuikaSvg name="x" />
              </KuikaButton>
            </KuikaTooltip>
          </div>
        </div>
        {!this.props.hideHeaderTabs && activeTab !== EColorPickerTab.AI && (
          <div className="kuika_custom-color-picker__title">
            <KuikaTextMedium>
              {activeTab === EColorPickerTab.Solid
                ? this.state.isEditMode
                  ? "Edit Color"
                  : "Solid Fill"
                : activeTab === EColorPickerTab.Gradient
                  ? "Gradient Fill"
                  : activeTab === EColorPickerTab.Image
                    ? "Image Fill"
                    : ""}
            </KuikaTextMedium>
          </div>
        )}
        <>{this.renderTabContent()}</>
        {!this.props.isSavedColorsHidden && showColorTabs && activeTab === EColorPickerTab.Solid && (
          <ColorList
            colorList={colorList}
            newColorName={newColorName}
            onNameChange={(value) => this.setState({ newColorName: value })}
            onAddColor={this.addColor}
            onDeleteColor={this.handleDeleteColor}
            onColorSelect={this.setFontColorFromList}
            currentHexColor={currentHexColor}
            onEditColor={this.handleEditColor}
            editingColor={this.state.editingColor || undefined}
            addColorPopoverVisible={this.state.isAddColorPopoverVisible}
            onAddColorPopoverVisibleChange={this.handleAddColorPopoverVisibleChange}
          />
        )}
      </div>
    );
  }
}

const colorPicker = withSD<IColorPickerProps>(ColorPicker);
export { colorPicker as ColorPicker };
